// Composant Checklist Dynamique des Documents
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { FiCheckCircle, FiCircle, FiAlertCircle, FiUpload } from 'react-icons/fi';

// Types
interface ChecklistItem {
  id: string;
  name: string;
  required: boolean;
  uploaded: boolean;
  verified: boolean;
  rejected: boolean;
  category: string;
}

interface DocumentChecklistProps {
  profileType: 'salarie' | 'entrepreneur' | 'informel';
  documents: any[];
  onUploadClick: (category: string) => void;
}

// Styled Components
const ChecklistContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.xl};
`;

const ChecklistHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const Title = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

const ProgressContainer = styled.div`
  flex: 1;
  max-width: 300px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  margin-bottom: ${theme.spacing.xs};
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${theme.colors.success};
  transition: width 0.5s ease;
`;

const ProgressText = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  text-align: right;
`;

const ChecklistItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const ChecklistItem = styled.div<{ status: 'completed' | 'uploaded' | 'rejected' | 'pending' }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background: ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success + '10';
      case 'uploaded': return theme.colors.info + '10';
      case 'rejected': return theme.colors.error + '10';
      default: return theme.colors.surface;
    }
  }};
  border-left: 3px solid ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success;
      case 'uploaded': return theme.colors.info;
      case 'rejected': return theme.colors.error;
      default: return theme.colors.text + '20';
    }
  }};
  transition: all ${theme.transitions.fast};

  &:hover {
    transform: translateX(4px);
  }
`;

const ItemIcon = styled.div<{ status: 'completed' | 'uploaded' | 'rejected' | 'pending' }>`
  font-size: 24px;
  color: ${props => {
    switch (props.status) {
      case 'completed': return theme.colors.success;
      case 'uploaded': return theme.colors.info;
      case 'rejected': return theme.colors.error;
      default: return theme.colors.text + '40';
    }
  }};
  flex-shrink: 0;
`;

const ItemContent = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ItemBadge = styled.span<{ required?: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  background: ${props => props.required ? theme.colors.error + '20' : theme.colors.info + '20'};
  color: ${props => props.required ? theme.colors.error : theme.colors.info};
  margin-left: ${theme.spacing.xs};
`;

const ItemStatus = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
`;

const UploadButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all ${theme.transitions.fast};
  flex-shrink: 0;

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }
`;

// Checklist par type de profil
const getChecklistByProfile = (profileType: string): ChecklistItem[] => {
  const baseDocuments: ChecklistItem[] = [
    {
      id: 'id-card',
      name: 'Pièce d\'identité',
      required: true,
      uploaded: false,
      verified: false,
      rejected: false,
      category: 'identite'
    },
    {
      id: 'proof-address',
      name: 'Justificatif de domicile',
      required: true,
      uploaded: false,
      verified: false,
      rejected: false,
      category: 'logement'
    },
    {
      id: 'tax-notice',
      name: 'Avis d\'imposition',
      required: true,
      uploaded: false,
      verified: false,
      rejected: false,
      category: 'revenus'
    }
  ];

  if (profileType === 'salarie') {
    return [
      ...baseDocuments,
      {
        id: 'payslips',
        name: 'Bulletins de salaire (3 derniers mois)',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'employment-contract',
        name: 'Contrat de travail',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'employer-certificate',
        name: 'Attestation employeur',
        required: false,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      }
    ];
  }

  if (profileType === 'entrepreneur') {
    return [
      ...baseDocuments,
      {
        id: 'kbis',
        name: 'Extrait KBIS ou SIRET',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'tax-declaration',
        name: 'Déclaration fiscale (2 dernières années)',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'bank-statements',
        name: 'Relevés bancaires (6 derniers mois)',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'accountant-certificate',
        name: 'Attestation comptable',
        required: false,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      }
    ];
  }

  if (profileType === 'informel') {
    return [
      ...baseDocuments,
      {
        id: 'income-declaration',
        name: 'Déclaration sur l\'honneur des revenus',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'bank-statements',
        name: 'Relevés bancaires (6 derniers mois)',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'witness-letter',
        name: 'Attestation de témoin (activité professionnelle)',
        required: false,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'revenus'
      },
      {
        id: 'guarantor',
        name: 'Garant (avec justificatifs)',
        required: true,
        uploaded: false,
        verified: false,
        rejected: false,
        category: 'autres'
      }
    ];
  }

  return baseDocuments;
};

export const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  profileType,
  documents,
  onUploadClick
}) => {
  const checklist = getChecklistByProfile(profileType);

  // Mettre à jour le statut selon les documents uploadés
  const updatedChecklist = checklist.map(item => {
    // Chercher un document correspondant au nom ou à l'ID du document requis
    const doc = documents.find(d => {
      // Matching par ID exact (si disponible)
      if (d.documentType && d.documentType === item.id) {
        return true;
      }
      // Matching par nom partiel (pour retrocompatibilité)
      const docNameLower = d.name.toLowerCase();
      const itemNameLower = item.name.toLowerCase();

      // Vérifications spécifiques par type de document
      if (item.id === 'id-card' && (docNameLower.includes('identité') || docNameLower.includes('cni') || docNameLower.includes('carte'))) {
        return true;
      }
      if (item.id === 'proof-address' && (docNameLower.includes('domicile') || docNameLower.includes('justificatif'))) {
        return true;
      }
      if (item.id === 'tax-notice' && (docNameLower.includes('imposition') || docNameLower.includes('avis'))) {
        return true;
      }
      if (item.id === 'payslips' && (docNameLower.includes('salaire') || docNameLower.includes('paie'))) {
        return true;
      }
      if (item.id === 'employment-contract' && (docNameLower.includes('contrat') || docNameLower.includes('travail'))) {
        return true;
      }
      if (item.id === 'employer-certificate' && (docNameLower.includes('attestation') && docNameLower.includes('employeur'))) {
        return true;
      }
      if (item.id === 'kbis' && (docNameLower.includes('kbis') || docNameLower.includes('siret'))) {
        return true;
      }
      if (item.id === 'tax-declaration' && (docNameLower.includes('déclaration') && docNameLower.includes('fiscale'))) {
        return true;
      }
      if (item.id === 'bank-statements' && (docNameLower.includes('relevé') || docNameLower.includes('bancaire'))) {
        return true;
      }
      if (item.id === 'accountant-certificate' && (docNameLower.includes('attestation') && docNameLower.includes('comptable'))) {
        return true;
      }
      if (item.id === 'income-declaration' && (docNameLower.includes('déclaration') && docNameLower.includes('honneur'))) {
        return true;
      }
      if (item.id === 'witness-letter' && (docNameLower.includes('témoin') || docNameLower.includes('attestation'))) {
        return true;
      }
      if (item.id === 'guarantor' && (docNameLower.includes('garant') || docNameLower.includes('caution'))) {
        return true;
      }

      return false;
    });

    if (doc) {
      return {
        ...item,
        uploaded: true,
        verified: doc.status === 'verified',
        rejected: doc.status === 'rejected'
      };
    }
    return item;
  });

  const totalItems = updatedChecklist.length;
  const completedItems = updatedChecklist.filter(item => item.verified).length;
  const progress = (completedItems / totalItems) * 100;

  const getItemStatus = (item: ChecklistItem): 'completed' | 'uploaded' | 'rejected' | 'pending' => {
    if (item.verified) return 'completed';
    if (item.rejected) return 'rejected';
    if (item.uploaded) return 'uploaded';
    return 'pending';
  };

  const getStatusText = (item: ChecklistItem): string => {
    if (item.verified) return 'Vérifié ✓';
    if (item.rejected) return 'Rejeté - À renouveler';
    if (item.uploaded) return 'En cours de vérification...';
    return 'À télécharger';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <FiCheckCircle />;
      case 'rejected': return <FiAlertCircle />;
      case 'uploaded': return <FiCheckCircle />;
      default: return <FiCircle />;
    }
  };

  return (
    <ChecklistContainer>
      <ChecklistHeader>
        <Title>Documents requis</Title>
        <ProgressContainer>
          <ProgressBar>
            <ProgressFill progress={progress} />
          </ProgressBar>
          <ProgressText>{completedItems} / {totalItems} documents vérifiés</ProgressText>
        </ProgressContainer>
      </ChecklistHeader>

      <ChecklistItems>
        {updatedChecklist.map(item => {
          const status = getItemStatus(item);
          return (
            <ChecklistItem key={item.id} status={status}>
              <ItemIcon status={status}>
                {getStatusIcon(status)}
              </ItemIcon>
              <ItemContent>
                <ItemName>
                  {item.name}
                  {item.required && <ItemBadge required>Obligatoire</ItemBadge>}
                  {!item.required && <ItemBadge>Optionnel</ItemBadge>}
                </ItemName>
                <ItemStatus>{getStatusText(item)}</ItemStatus>
              </ItemContent>
              {!item.verified && (
                <UploadButton onClick={() => onUploadClick(item.category)}>
                  <FiUpload />
                  {item.uploaded ? 'Remplacer' : 'Télécharger'}
                </UploadButton>
              )}
            </ChecklistItem>
          );
        })}
      </ChecklistItems>
    </ChecklistContainer>
  );
};
